import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft, Globe, Feather, Pencil, RefreshCw, Shield, LogOut, Trash2,
    ChevronRight, Loader2, Ban, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { updateSettings, cancelSubscription, resumeSubscription, deleteAccount, billingMe } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTranslation } from "@/lib/i18n";
import { useEffect } from "react";
import PrivacySummary from "@/components/PrivacySummary";
import { MODELS, effectiveModel, isModelAllowed } from "@/lib/models";


export default function Settings() {
    const nav = useNavigate();
    const { user, refresh, logout } = useAuth();
    const { t, setLang } = useTranslation();
    const [me, setMe] = useState(null);
    const [busy, setBusy] = useState(false);
    const [modelOpen, setModelOpen] = useState(false);
    const [privacyOpen, setPrivacyOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState("");

    useEffect(() => {
        billingMe().then(setMe).catch(() => {});
    }, []);

    const initials = (user?.name || user?.email || "?").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();
    const isPaid = me && !me.is_admin && me.plan !== "thoughts";
    const cancelScheduled = isPaid && me?.cancel_at_period_end;
    // The cancellation copy said "Magnus" whatever the plan.
    const planLabel = me?.plan_label || me?.limits?.label || "Your plan";
    const accessUntilLabel = me?.plan_until ? new Date(me.plan_until).toLocaleDateString() : "—";
    const allowedModels = me?.allowed_models;
    const modelValue = effectiveModel(user?.preferred_model, allowedModels);
    const currentModel = MODELS.find(m => m.value === modelValue) || MODELS[0];
    const distractionFree = user?.writing_mode === "distraction_free";

    async function patchSettings(patch, optimisticMsg) {
        setBusy(true);
        try {
            await updateSettings(patch);
            await refresh();
            if (optimisticMsg) toast.success(optimisticMsg);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't save");
        }
        setBusy(false);
    }

    async function pickLanguage(next) {
        setLang(next);
        await patchSettings({ language: next }, next === "en" ? "Language: English" : "Idioma: Español");
    }

    async function pickModel(next) {
        setModelOpen(false);
        await patchSettings({ preferred_model: next }, "AI model updated");
    }

    async function toggleWritingMode(checked) {
        await patchSettings(
            { writing_mode: checked ? "distraction_free" : "default" },
            checked ? "Distraction-free mode on" : "Standard editor",
        );
    }

    async function doCancel() {
        setBusy(true);
        try {
            await cancelSubscription();
            const fresh = await billingMe();
            setMe(fresh);
            toast.success(`Cancelled — ${planLabel} stays active until ${fresh.plan_until ? new Date(fresh.plan_until).toLocaleDateString() : "period end"}`);
            setCancelOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Cancel failed");
        }
        setBusy(false);
    }

    async function doResume() {
        setBusy(true);
        try {
            await resumeSubscription();
            const fresh = await billingMe();
            setMe(fresh);
            toast.success("Subscription resumed");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Resume failed");
        }
        setBusy(false);
    }

    async function doDelete() {
        if (deleteConfirm.trim() !== "DELETE") {
            toast.error("Type DELETE to confirm");
            return;
        }
        setBusy(true);
        try {
            await deleteAccount();
            toast.success("Account deleted");
            await logout();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
        setBusy(false);
    }

    return (
        <div className="min-h-screen bg-background pb-16">
            <header className="px-6 pt-6 pb-4 max-w-3xl mx-auto flex items-center gap-3">
                <Button data-testid="settings-back" variant="ghost" size="sm" className="w-10 h-10 rounded-full p-0"
                    onClick={() => nav("/")}>
                    <ArrowLeft className="w-4 h-4" />
                </Button>
                <h1 className="font-heading text-3xl">{t("settings.title")}</h1>
            </header>

            <main className="max-w-3xl mx-auto px-6 space-y-4">
                {/* User card */}
                <Card data-testid="settings-user-card" className="p-4 flex items-center gap-4 bg-card/50">
                    <Avatar className="w-12 h-12">
                        {user?.picture && <AvatarImage src={user.picture} />}
                        <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                        <div className="font-semibold truncate">{user?.name || "—"}</div>
                        <div className="text-sm text-muted-foreground truncate">{user?.email}</div>
                    </div>
                </Card>

                {/* Language */}
                <SectionCard>
                    <Row icon={Globe} title={t("settings.language.title")} subtitle={t("settings.language.subtitle")} />
                    <div className="grid grid-cols-2 gap-2 mt-3">
                        <LangButton active={user?.language !== "es"} onClick={() => pickLanguage("en")} testid="lang-en">
                            {t("settings.language.english")}
                        </LangButton>
                        <LangButton active={user?.language === "es"} onClick={() => pickLanguage("es")} testid="lang-es">
                            {t("settings.language.spanish")}
                        </LangButton>
                    </div>
                </SectionCard>

                {/* AI Model */}
                <SectionCard>
                    <RowButton
                        testid="settings-ai-model"
                        icon={Feather}
                        title={t("settings.ai.title")}
                        subtitle={`${currentModel.label} — ${t("settings.ai.subtitle")}`}
                        onClick={() => setModelOpen(true)}
                    />
                </SectionCard>

                {/* Writing Mode */}
                <SectionCard>
                    <div className="flex items-center gap-3">
                        <IconBubble icon={Pencil} />
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">{t("settings.writing.title")}</div>
                            <div className="text-xs text-muted-foreground">
                                {distractionFree ? t("settings.writing.subtitle.distraction") : t("settings.writing.subtitle.default")}
                            </div>
                        </div>
                        <Switch
                            data-testid="settings-writing-toggle"
                            checked={distractionFree}
                            onCheckedChange={toggleWritingMode}
                            disabled={busy}
                        />
                    </div>
                </SectionCard>

                {/* Sync (info-only) */}
                <SectionCard>
                    <Row icon={RefreshCw} title={t("settings.sync.title")} subtitle={t("settings.sync.subtitle")} />
                </SectionCard>

                {/* Privacy Policy */}
                <SectionCard>
                    <RowButton
                        testid="settings-privacy"
                        icon={Shield}
                        title={t("settings.privacy.title")}
                        subtitle={t("settings.privacy.subtitle")}
                        onClick={() => setPrivacyOpen(true)}
                    />
                </SectionCard>

                {/* Cancel subscription (paid users only) */}
                {isPaid && !cancelScheduled && (
                    <SectionCard>
                        <RowButton
                            testid="settings-cancel-subscription"
                            icon={Ban}
                            title={t("settings.cancel.title")}
                            subtitle={t("settings.cancel.subtitle")}
                            tone="danger"
                            onClick={() => setCancelOpen(true)}
                        />
                    </SectionCard>
                )}
                {isPaid && cancelScheduled && (
                    <SectionCard>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
                                <Ban className="w-4 h-4 text-amber-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-sm">{t("settings.cancel.scheduled.title")}</div>
                                <div className="text-xs text-muted-foreground">
                                    {t("settings.cancel.scheduled.subtitle").replace("{plan}", planLabel).replace("{date}", accessUntilLabel)}
                                </div>
                            </div>
                            <Button
                                data-testid="settings-resume-subscription"
                                variant="outline"
                                size="sm"
                                className="rounded-full text-xs"
                                onClick={doResume}
                                disabled={busy}
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                {t("settings.cancel.resume")}
                            </Button>
                        </div>
                    </SectionCard>
                )}

                {/* Sign Out */}
                <button
                    data-testid="settings-sign-out"
                    className="w-full rounded-xl border border-border/60 bg-card/40 py-4 flex items-center justify-center gap-2 text-sm text-destructive hover:bg-destructive/5 transition-colors"
                    onClick={logout}
                >
                    <LogOut className="w-4 h-4" /> {t("settings.signOut")}
                </button>

                {/* Delete Account */}
                <button
                    data-testid="settings-delete-account"
                    className="w-full pt-2 pb-6 flex items-center justify-center gap-2 text-sm text-destructive hover:underline"
                    onClick={() => setDeleteOpen(true)}
                >
                    <Trash2 className="w-4 h-4" /> {t("settings.deleteAccount")}
                </button>
            </main>

            {/* AI Model picker */}
            <Dialog open={modelOpen} onOpenChange={setModelOpen}>
                <DialogContent data-testid="model-picker-dialog" className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl font-normal flex items-center gap-2">
                            <Feather className="w-5 h-5 text-primary" /> {t("settings.ai.title")}
                        </DialogTitle>
                    </DialogHeader>
                    <Select value={modelValue} onValueChange={pickModel}>
                        <SelectTrigger data-testid="model-picker-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {MODELS.map(m => {
                                const allowed = isModelAllowed(m.value, allowedModels);
                                return (
                                    <SelectItem key={m.value} value={m.value} disabled={!allowed} data-testid={`model-option-${m.value}`}>
                                        {m.label}{allowed ? "" : " · Magnus and up"}
                                    </SelectItem>
                                );
                            })}
                        </SelectContent>
                    </Select>
                </DialogContent>
            </Dialog>

            {/* Privacy policy dialog */}
            <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
                <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl font-normal flex items-center gap-2">
                            <Shield className="w-5 h-5 text-primary" /> {t("settings.privacy.title")}
                        </DialogTitle>
                        <DialogDescription>A summary of how Day Dreamer handles your data.</DialogDescription>
                    </DialogHeader>
                    <PrivacySummary onNavigate={() => setPrivacyOpen(false)} />
                </DialogContent>
            </Dialog>

            {/* Cancel subscription confirmation */}
            <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
                <DialogContent data-testid="cancel-confirm-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl font-normal">
                            {t("settings.cancel.confirmTitle")}
                        </DialogTitle>
                        <DialogDescription>{t("settings.cancel.confirmBody").replace("{plan}", planLabel)}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" className="rounded-full" onClick={() => setCancelOpen(false)} disabled={busy}>
                            {t("settings.cancel.confirmNo")}
                        </Button>
                        <Button data-testid="cancel-confirm-yes" variant="destructive" className="rounded-full" onClick={doCancel} disabled={busy}>
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            {t("settings.cancel.confirmYes")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete account confirmation */}
            <Dialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) setDeleteConfirm(""); }}>
                <DialogContent data-testid="delete-confirm-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl font-normal text-destructive">
                            {t("settings.deleteAccount.confirmTitle")}
                        </DialogTitle>
                        <DialogDescription>{t("settings.deleteAccount.confirmBody")}</DialogDescription>
                    </DialogHeader>
                    <Input
                        data-testid="delete-confirm-input"
                        placeholder={t("settings.deleteAccount.confirmInput")}
                        value={deleteConfirm}
                        onChange={(e) => setDeleteConfirm(e.target.value)}
                        className="mt-2"
                    />
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" className="rounded-full" onClick={() => setDeleteOpen(false)} disabled={busy}>
                            {t("settings.deleteAccount.confirmNo")}
                        </Button>
                        <Button
                            data-testid="delete-confirm-yes"
                            variant="destructive"
                            className="rounded-full"
                            onClick={doDelete}
                            disabled={busy || deleteConfirm.trim() !== "DELETE"}
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            {t("settings.deleteAccount.confirmYes")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function SectionCard({ children }) {
    return <Card className="p-4 bg-card/50">{children}</Card>;
}

function IconBubble({ icon: Icon }) {
    return (
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-primary" />
        </div>
    );
}

function Row({ icon, title, subtitle }) {
    return (
        <div className="flex items-center gap-3">
            <IconBubble icon={icon} />
            <div className="min-w-0">
                <div className="font-medium text-sm">{title}</div>
                <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
            </div>
        </div>
    );
}

function RowButton({ testid, icon: Icon, title, subtitle, tone, onClick }) {
    const isDanger = tone === "danger";
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className="w-full flex items-center gap-3 text-left"
        >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isDanger ? "bg-destructive/15" : "bg-primary/10"}`}>
                <Icon className={`w-4 h-4 ${isDanger ? "text-destructive" : "text-primary"}`} />
            </div>
            <div className="min-w-0 flex-1">
                <div className={`font-medium text-sm ${isDanger ? "text-destructive" : ""}`}>{title}</div>
                <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        </button>
    );
}

function LangButton({ active, onClick, testid, children }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active
                    ? "bg-primary text-primary-foreground"
                    : "bg-background/60 text-foreground/80 hover:bg-background"
            }`}
        >
            {children}
        </button>
    );
}
